import * as React from 'react';

import type { PageProps } from 'gatsby';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Underline } from 'stile-shared/src/components/2024/common/Underline';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const NorthCarolinaAlignmentLayout = styled<typeof PageLayout>(PageLayout)`
  .main-content {
    margin-top: -12px;
  }
`;

const NorthCarolinaAlignmentTopSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    @media (max-width: 768px) {
      padding-bottom: 48px;
    }

    h1 {
      margin-top: 80px;
      margin-bottom: 60px;
      width: 60%;

      @media (max-width: 768px) {
        margin-top: 38px;
        margin-bottom: 25px;
        width: 100%;
      }
    }

    > div {
      width: 38%;

      @media (max-width: 768px) {
        width: 100%;
      }
    }

    .underline {
      margin-top: 20px;
      margin-bottom: 30px;
    }
  }
`;

const SwipeAdviceSection = styled.div`
  .content {
    display: none;
    flex-direction: row;
    gap: 10px;
    padding-bottom: 12px;
    color: #706d69;
    p {
      margin: 0;
      padding: 0;
    }

    @media (max-width: 768px) {
      display: flex;
    }
  }
`;

const AlignmentSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    .table {
      border-radius: 4px;
      border: var(--stile-grey-8) 1px solid;
      display: grid;
      grid-template-columns: 200px 100px 200px 200px 100px 1fr;
      overflow-x: scroll;
      .header {
        display: contents;
        border-radius: 4px;
        > div {
          background: var(--stile-grey-2);
          padding: 10px;
          font-weight: 600;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;
          &:first-child {
            border-top-left-radius: 4px;
            background: var(--stile-grey-4);
          }
          &:last-child {
            border-top-right-radius: 4px;
          }
        }
      }
      .row {
        display: contents;

        > div {
          padding: 10px;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;

          &:first-child {
            background-color: #f0fef1;
          }
        }

        // every even child
        &:nth-child(odd) {
          > div {
            background-color: #fbfaf9;

            &:first-child {
              background-color: #d9f7da;
            }
          }
        }

        &.first-in-section {
          > div {
            border-top: var(--stile-grey-8) 1px solid;
            border-collapse: collapse;
          }
        }

        &:last-child {
          > div {
            border-bottom: none;
          }
        }
      }
    }
  }
`;

function TwentyTwentyFourOregonPage(props: PageProps) {
  return (
    <NorthCarolinaAlignmentLayout {...props}>
      <NorthCarolinaAlignmentTopSection>
        <div className="content">
          <h1>North Carolina Science Standards Alignment</h1>
          <div>
            Stile is approved by the NCDPI for 6th, 7th, and 8th grades.. With Stile, you&apos;ve
            got the Science Standards covered.
          </div>
          <Underline underlineNumber={1} speed="0.5s" />
          <Button
            style="primary"
            href="/states/north-carolina/"
            label="Learn about Stile in North Carolina"
          />
        </div>
      </NorthCarolinaAlignmentTopSection>
      <SwipeAdviceSection>
        <div className="content">
          <p>Swipe across to view table</p>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.24648 5.35416H5.94857C6.1201 5.35416 6.26194 5.41023 6.37409 5.52239C6.48624 5.63454 6.54232 5.77638 6.54232 5.94791C6.54232 6.11943 6.48624 6.26127 6.37409 6.37343C6.26194 6.48558 6.1201 6.54166 5.94857 6.54166H3.37565C3.15135 6.54166 2.96332 6.46579 2.81159 6.31405C2.65985 6.16232 2.58398 5.9743 2.58398 5.74999V3.17707C2.58398 3.00555 2.64006 2.86371 2.75221 2.75155C2.86437 2.6394 3.00621 2.58332 3.17773 2.58332C3.34926 2.58332 3.4911 2.6394 3.60326 2.75155C3.71541 2.86371 3.77148 3.00555 3.77148 3.17707V4.18645C4.72148 3.40798 5.77044 2.81423 6.91836 2.4052C8.06628 1.99617 9.26037 1.79166 10.5007 1.79166C12.2687 1.79166 13.852 2.1677 15.2507 2.91978C16.6493 3.67186 17.6257 4.66145 18.1798 5.88853C18.2458 6.04686 18.2392 6.1953 18.16 6.33384C18.0809 6.47239 17.9621 6.54166 17.8038 6.54166C17.6059 6.54166 17.4244 6.48888 17.2595 6.38332C17.0946 6.27777 16.9593 6.13923 16.8538 5.9677C16.26 5.03089 15.409 4.2986 14.3007 3.77082C13.1923 3.24305 11.9257 2.97916 10.5007 2.97916C9.33954 2.97916 8.22461 3.18367 7.15586 3.5927C6.08711 4.00173 5.11732 4.58888 4.24648 5.35416ZM10.3621 18.4167C10.0454 18.4167 9.74197 18.3573 9.45169 18.2385C9.16142 18.1198 8.90412 17.9483 8.67982 17.7239L5.19648 14.2208C5.05135 14.0757 4.97548 13.8943 4.96888 13.6766C4.96228 13.4588 5.03155 13.2708 5.17669 13.1125L5.23607 13.0531C5.44718 12.842 5.69458 12.7002 5.97826 12.6276C6.26194 12.555 6.54232 12.5583 6.8194 12.6375L8.12565 13.0135V6.54166C8.12565 6.31735 8.20152 6.12933 8.35326 5.97759C8.50499 5.82586 8.69301 5.74999 8.91732 5.74999C9.14162 5.74999 9.32965 5.82586 9.48138 5.97759C9.63312 6.12933 9.70899 6.31735 9.70899 6.54166V14.0625C9.70899 14.3264 9.60343 14.5375 9.39232 14.6958C9.18121 14.8542 8.9503 14.9003 8.69961 14.8344L7.78919 14.5771L9.80794 16.5958C9.87392 16.6618 9.95638 16.7179 10.0553 16.7641C10.1543 16.8102 10.2566 16.8333 10.3621 16.8333H13.6673C14.1027 16.8333 14.4755 16.6783 14.7855 16.3682C15.0956 16.0581 15.2507 15.6854 15.2507 15.25V12.0833C15.2507 11.859 15.3265 11.671 15.4783 11.5193C15.63 11.3675 15.818 11.2917 16.0423 11.2917C16.2666 11.2917 16.4546 11.3675 16.6064 11.5193C16.7581 11.671 16.834 11.859 16.834 12.0833V15.25C16.834 16.1208 16.5239 16.8663 15.9038 17.4864C15.2836 18.1066 14.5382 18.4167 13.6673 18.4167H10.3621ZM11.2923 8.91666C11.5166 8.91666 11.7046 8.99252 11.8564 9.14426C12.0081 9.296 12.084 9.48402 12.084 9.70832V12.0833C12.084 12.3076 12.0081 12.4956 11.8564 12.6474C11.7046 12.7991 11.5166 12.875 11.2923 12.875C11.068 12.875 10.88 12.7991 10.7283 12.6474C10.5765 12.4956 10.5007 12.3076 10.5007 12.0833V9.70832C10.5007 9.48402 10.5765 9.296 10.7283 9.14426C10.88 8.99252 11.068 8.91666 11.2923 8.91666ZM13.6673 9.70832C13.8916 9.70832 14.0796 9.78419 14.2314 9.93593C14.3831 10.0877 14.459 10.2757 14.459 10.5V12.0833C14.459 12.3076 14.3831 12.4956 14.2314 12.6474C14.0796 12.7991 13.8916 12.875 13.6673 12.875C13.443 12.875 13.255 12.7991 13.1033 12.6474C12.9515 12.4956 12.8757 12.3076 12.8757 12.0833V10.5C12.8757 10.2757 12.9515 10.0877 13.1033 9.93593C13.255 9.78419 13.443 9.70832 13.6673 9.70832Z"
              fill="#928D88"
            />
          </svg>
        </div>
      </SwipeAdviceSection>
      <AlignmentSection>
        <div className="content">
          <div className="table">
            <div className="header">
              <div>Stile unit</div>
              <div>Grade</div>
              <div>Strand</div>
              <div>Standard</div>
              <div>Sub-Standard</div>
              <div>Objectives</div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>6</div>
              <div>Matter and its Interactions</div>
              <div>PS.6.1 Understand the structure, states, and physical properties of matter.</div>
              <div>PS.6.1.1</div>
              <div>
                Use models to illustrate that matter is made of atoms and elements, and are
                distinguished from each other by the types of atoms that compose them.
              </div>
            </div>
            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Matter and its Interactions</div>
              <div>PS.6.1 Understand the structure, states, and physical properties of matter.</div>
              <div>PS.6.1.2</div>
              <div>
                Use models to explain the relationship between changes in thermal energy in a
                substance and the motion of its particles (including phase changes).
              </div>
            </div>
            <div className="row">
              <div>States of Matter</div>
              <div>6</div>
              <div>Matter and its Interactions</div>
              <div>PS.6.1 Understand the structure, states, and physical properties of matter.</div>
              <div>PS.6.1.3</div>
              <div>
                Carry out investigations to compare the physical properties of pure substances that
                are independent of the amount of matter present including density, melting point,
                boiling point and solubility to properties that are dependent on the amount of
                matter present to include volume, mass and weight.
              </div>
            </div>
            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>
                PS.6.2 Understand characteristics of thermal and electrical energy transfer and
                interactions of matter and energy.
              </div>
              <div>PS.6.2.1</div>
              <div>
                Use models to compare the directional transfer of heat energy of matter through
                convection, radiation, and conduction.
              </div>
            </div>
            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>
                PS.6.2 Understand characteristics of thermal and electrical energy transfer and
                interactions of matter and energy.
              </div>
              <div>PS.6.2.2</div>
              <div>
                Use models to explain how the transfer of heat and resulting change of temperature
                impacts the behavior of matter to include expansion, and contraction.
              </div>
            </div>
            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>
                PS.6.2 Understand characteristics of thermal and electrical energy transfer and
                interactions of matter and energy.
              </div>
              <div>PS.6.2.3</div>
              <div>
                Carry out investigations to compare the transfer of thermal energy in insulated and
                non-insulated materials (examples could include insulated box, solar cooker, or
                styrofoam cup).
              </div>
            </div>
            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>
                PS.6.2 Understand characteristics of thermal and electrical energy transfer and
                interactions of matter and energy.
              </div>
              <div>PS.6.2.4</div>
              <div>
                Engage in argument from evidence to classify materials as conductors and insulators
                of energy (both thermal and electrical).
              </div>
            </div>
            <div className="row">
              <div>Electrical Circuits</div>
              <div>6</div>
              <div>Energy</div>
              <div>
                PS.6.2 Understand characteristics of thermal and electrical energy transfer and
                interactions of matter and energy.
              </div>
              <div>PS.6.2.5</div>
              <div>
                Carry out investigations to explain the transfer of electrical energy in electrical
                circuits, to include how a circuit requires a complete loop through which an
                electrical current can pass.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>6</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>
                PS.6.3 Understand the properties of waves and the wavelike property of energy in
                seismic, electromagnetic (including visible light), and sound waves.
              </div>
              <div>PS.6.3.1</div>
              <div>
                Use models of a simple wave to explain wave properties in seismic, light, and sound
                waves that include: waves having a repeating pattern with a specific amplitude,
                frequency, and wavelength, and the amplitude of a wave is related to the energy of
                the wave.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>6</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>
                PS.6.3 Understand the properties of waves and the wavelike property of energy in
                seismic, electromagnetic (including visible light), and sound waves.
              </div>
              <div>PS.6.3.2</div>
              <div>
                Carry out investigations to conclude the relationship between the electromagnetic
                spectrum (including visible light) and sight.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>6</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>
                PS.6.3 Understand the properties of waves and the wavelike property of energy in
                seismic, electromagnetic (including visible light), and sound waves.
              </div>
              <div>PS.6.3.3</div>
              <div>
                Carry out investigations to conclude the relationship between sound waves (including
                rate of vibration, the medium through which vibrations travel) and hearing.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>6</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>
                PS.6.3 Understand the properties of waves and the wavelike property of energy in
                seismic, electromagnetic (including visible light), and sound waves.
              </div>
              <div>PS.6.3.4</div>
              <div>
                Use models to explain that various waves (seismic, sound, electromagnetic, including
                visible light) are reflected, absorbed or transmitted through various materials.
              </div>
            </div>
            <div className="row">
              <div>Plants</div>
              <div>6</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.6.1 Understand the structures, processes, and behaviors of plants that enable
                them to survive and reproduce.
              </div>
              <div>LS.6.1.1</div>
              <div>
                Use models to explain how the processes of photosynthesis, respiration, and
                transpiration work together to meet the needs of plants.
              </div>
            </div>
            <div className="row">
              <div>Plants</div>
              <div>6</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.6.1 Understand the structures, processes, and behaviors of plants that enable
                them to survive and reproduce.
              </div>
              <div>LS.6.1.2</div>
              <div>
                Construct an explanation to compare how vascular and nonvascular plants obtain,
                transport, and use nutrients and water necessary for survival.
              </div>
            </div>
            <div className="row">
              <div>Plants</div>
              <div>6</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.6.1 Understand the structures, processes, and behaviors of plants that enable
                them to survive and reproduce.
              </div>
              <div>LS.6.1.3</div>
              <div>
                Use models to summarize structural adaptations, processes, and responses that
                flowering plants use for defense, survival and reproduction.
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Ecosystems - Interactions, Energy, and Dynamics</div>
              <div>
                Understand the flow of energy through ecosystems and the responses of populations to
                the biotic and abiotic factors in their environment.
              </div>
              <div>LS.6.2.1</div>
              <div>
                Use models to summarize how energy derived from the sun is used by plants to produce
                sugars (photosynthesis) and is transferred to consumers and decomposers.
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Ecosystems - Interactions, Energy, and Dynamics</div>
              <div>
                Understand the flow of energy through ecosystems and the responses of populations to
                the biotic and abiotic factors in their environment.
              </div>
              <div>LS.6.2.2</div>
              <div>
                Analyze and interpret data to predict how the abiotic factors (such as temperature,
                water, sunlight, and soil quality) and biotic factors affect the ability of
                organisms to grow and survive in different biomes (freshwater, marine, temperate
                forest, rainforest, grassland, desert, taiga, tundra).
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                Understand the earth/moon/sun system, and the properties, structures and predictable
                motions of celestial bodies in the Universe.
              </div>
              <div>ESS.6.1.1</div>
              <div>
                Use models to explain how the relative motion and relative position of the Sun,
                Earth and moon affect the seasons, tides, phases of the moon, and eclipses.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                Understand the earth/moon/sun system, and the properties, structures and predictable
                motions of celestial bodies in the Universe.
              </div>
              <div>ESS.6.1.2</div>
              <div>
                Analyze and interpret data to compare the planets in our solar system in terms of:
                size and gravitational force relative to Earth, surface and atmospheric features,
                relative distance from the sun, and ability to support life.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                Understand the earth/moon/sun system, and the properties, structures and predictable
                motions of celestial bodies in the Universe.
              </div>
              <div>ESS.6.1.3</div>
              <div>
                Use models to explain how the gravitational forces of the Sun and planets impact the
                structure of our solar system.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                Understand the earth/moon/sun system, and the properties, structures and predictable
                motions of celestial bodies in the Universe.
              </div>
              <div>ESS.6.1.4</div>
              <div>
                Analyze and interpret data from historical and ongoing space exploration to
                illustrate the size and scale of the components of our solar system, galaxy, and
                universe.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>
                Understand the lithosphere and how interactions of constructive and destructive
                forces have resulted in changes in the surface of the earth over time.
              </div>
              <div>ESS.6.2.1</div>
              <div>
                Use models to summarize the structure of the earth, including the layers, the mantle
                and core based on the relative position, composition and density.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>
                Understand the lithosphere and how interactions of constructive and destructive
                forces have resulted in changes in the surface of the earth over time.
              </div>
              <div>ESS.6.2.2</div>
              <div>
                Construct an explanation to illustrate how the movement of lithospheric plates can
                create geologic landforms and cause major geologic events such as earthquakes and
                volcanic eruptions.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>
                Understand the lithosphere and how interactions of constructive and destructive
                forces have resulted in changes in the surface of the earth over time.
              </div>
              <div>ESS.6.2.3</div>
              <div>
                Use models to explain the rock cycle and its relationship to the formation of soil
                (including how different types of soil come from different types of rocks).
              </div>
            </div>
            <div className="row">
              <div>Resources</div>
              <div>6</div>
              <div>Earth and Human Activity</div>
              <div>Understand the reciprocal relationship between the lithosphere and humans.</div>
              <div>ESS.6.3.1</div>
              <div>
                Engage in argument from evidence to explain that the good health of humans and the
                environment requires: monitoring of the lithosphere, maintaining soil quality and
                stewardship.
              </div>
            </div>
            <div className="row">
              <div>Resources</div>
              <div>6</div>
              <div>Earth and Human Activity</div>
              <div>Understand the reciprocal relationship between the lithosphere and humans.</div>
              <div>ESS.6.3.2</div>
              <div>
                Obtain, evaluate, and communicate information to compare the implications of
                sustainable and unsustainable land use practices (including agriculture and
                deforestation) and the importance of stewardship.
              </div>
            </div>
            <div className="row">
              <div>Kinematics</div>
              <div>7</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>
                PS.7.1 Understand motion, the effects of forces on motion, and the graphical
                representations of motion.
              </div>
              <div>PS.7.1.1</div>
              <div>
                Construct an explanation to summarize the motion of an object by its position,
                direction of motion, and speed in respect to some other object.
              </div>
            </div>

            <div className="row">
              <div>Forces</div>
              <div>7</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>
                PS.7.1 Understand motion, the effects of forces on motion, and the graphical
                representations of motion.
              </div>
              <div>PS.7.1.2</div>
              <div>
                Use models to illustrate the effects of balanced and unbalanced forces acting on an
                object (including friction, gravity, and magnetism).
              </div>
            </div>

            <div className="row">
              <div>Kinematics</div>
              <div>7</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>
                PS.7.1 Understand motion, the effects of forces on motion, and the graphical
                representations of motion.
              </div>
              <div>PS.7.1.3</div>
              <div>
                Analyze and interpret graphical data to summarize the motion of an object to show a
                change in position over a period of time.
              </div>
            </div>

            <div className="row">
              <div>Kinematics</div>
              <div>7</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>
                PS.7.1 Understand motion, the effects of forces on motion, and the graphical
                representations of motion.
              </div>
              <div>PS.7.1.4</div>
              <div>
                Analyze and interpret graphical data to summarize the motion of an object to show a
                change in distance over a period of time for constant speed and variable motion.
              </div>
            </div>

            <div className="row">
              <div>Energy</div>
              <div>7</div>
              <div>Energy</div>
              <div>
                PS.7.2 Understand forms of energy, energy transfer and transformation, and
                conservation in mechanical systems.
              </div>
              <div>PS.7.2.1</div>
              <div>
                Construct an explanation to summarize how kinetic and potential energy contribute to
                the mechanical energy of an object.
              </div>
            </div>

            <div className="row">
              <div>Energy</div>
              <div>7</div>
              <div>Energy</div>
              <div>
                PS.7.2 Understand forms of energy, energy transfer and transformation, and
                conservation in mechanical systems.
              </div>
              <div>PS.7.2.2</div>
              <div>
                Engage in argument from evidence to explain how energy can be transformed from one
                form to another, specifically potential energy and kinetic energy (models could
                include roller coasters, pendulums, or cars on ramps as examples).
              </div>
            </div>

            <div className="row">
              <div>Energy</div>
              <div>7</div>
              <div>Energy</div>
              <div>
                PS.7.2 Understand forms of energy, energy transfer and transformation, and
                conservation in mechanical systems.
              </div>
              <div>PS.7.2.3</div>
              <div>
                Carry out investigations to conclude that energy can be transferred from one system
                to another when two objects push or pull on each other over a distance (work) in a
                mechanical system using qualitative data.
              </div>
            </div>

            <div className="row">
              <div>Forces</div>
              <div>7</div>
              <div>Energy</div>
              <div>
                PS.7.2 Understand forms of energy, energy transfer and transformation, and
                conservation in mechanical systems.
              </div>
              <div>PS.7.2.4</div>
              <div>
                Carry out investigations to compare the efficiency of simple machines in relation to
                their advantages for particular purposes (to include inclined planes, pulleys,
                levers and wheel and axles) using qualitative data.
              </div>
            </div>

            <div className="row">
              <div>Cells</div>
              <div>7</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.7.1 Understand the processes, structures and functions of living organisms that
                enable them to survive, reproduce and carry out the basic functions of life.
              </div>
              <div>LS.7.1.1</div>
              <div>
                Construct an explanation to conclude how the structures of single-celled organisms
                carry out all of the basic functions of life including: Euglena, Amoeba, Paramecium,
                Volvox.
              </div>
            </div>

            <div className="row">
              <div>Cells</div>
              <div>7</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.7.1 Understand the processes, structures and functions of living organisms that
                enable them to survive, reproduce and carry out the basic functions of life.
              </div>
              <div>LS.7.1.2</div>
              <div>
                Use models to explain how the relevant structures within cells (including cell
                membrane, cell wall, nucleus, mitochondria, chloroplasts, and vacuoles) function to
                support the life of plant, animal, and bacterial cells.
              </div>
            </div>

            <div className="row">
              <div>Cells</div>
              <div>7</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.7.1 Understand the processes, structures and functions of living organisms that
                enable them to survive, reproduce and carry out the basic functions of life.
              </div>
              <div>LS.7.1.3</div>
              <div>
                Use models to explain how the hierarchical organization of multicellular organisms
                from cells to tissues to organs to systems to organisms functions to support life.
              </div>
            </div>

            <div className="row">
              <div>Cells</div>
              <div>7</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.7.1 Understand the processes, structures and functions of living organisms that
                enable them to survive, reproduce and carry out the basic functions of life.
              </div>
              <div>LS.7.1.4</div>
              <div>
                Construct an explanation to summarize how the major systems of the human body
                interact with each other to support life (including digestion, respiration,
                reproduction, circulation, excretion, nervous).
              </div>
            </div>

            <div className="row">
              <div>Genetics</div>
              <div>7</div>
              <div>Heredity- Inheritance and Variation of Traits</div>
              <div>
                LS.7.2 Understand the relationship of the mechanisms of reproduction, patterns of
                inheritance, and potential variation among offspring.
              </div>
              <div>LS.7.2.1</div>
              <div>
                Construct an explanation supported with scientific evidence to summarize the role of
                genes on chromosomes as inherited cellular structures which contribute to an
                organism's traits (not to include the structure of DNA).
              </div>
            </div>

            <div className="row">
              <div>Genetics</div>
              <div>7</div>
              <div>Heredity- Inheritance and Variation of Traits</div>
              <div>
                LS.7.2 Understand the relationship of the mechanisms of reproduction, patterns of
                inheritance, and potential variation among offspring.
              </div>
              <div>LS.7.2.2</div>
              <div>
                Use models to explain how asexual reproduction results in offspring with identical
                genetic information while sexual reproduction results in offspring with genetic
                variation (not to include specific phases of mitosis and meiosis).
              </div>
            </div>

            <div className="row">
              <div>Genetics</div>
              <div>7</div>
              <div>Heredity- Inheritance and Variation of Traits</div>
              <div>
                LS.7.2 Understand the relationship of the mechanisms of reproduction, patterns of
                inheritance, and potential variation among offspring.
              </div>
              <div>LS.7.2.3</div>
              <div>
                Use models (Punnett squares) to infer and predict patterns of the inheritance of
                single genetic traits from parent to offspring (including dominant and recessive
                traits).
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.7.1 Understand the atmosphere and how the cycling of water relates to Earth’s
                weather and climate.
              </div>
              <div>ESS.7.1.1</div>
              <div>
                Analyze and interpret data to compare the composition, properties and structure of
                Earth’s atmosphere to include: mixtures of gases and differences in temperature and
                pressure within layers.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.7.1 Understand the atmosphere and how the cycling of water relates to Earth’s
                weather and climate.
              </div>
              <div>ESS.7.1.2</div>
              <div>
                Use models to explain how the energy of the Sun and Earth’s gravity drive the
                cycling of water, including changes of state, as it moves through multiple pathways
                in Earth’s systems and relates to weather patterns on Earth.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.7.1 Understand the atmosphere and how the cycling of water relates to Earth’s
                weather and climate.
              </div>
              <div>ESS.7.1.3</div>
              <div>
                Analyze and interpret data to explain the relationship between the movement of air
                masses, high and low pressure systems, frontal boundaries and weather conditions
                that may result.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.7.1 Understand the atmosphere and how the cycling of water relates to Earth’s
                weather and climate.
              </div>
              <div>ESS.7.1.4</div>
              <div>
                Use models to predict weather conditions based on observations (including clouds,
                air masses, fronts), measurements (wind speed and direction, air temperature,
                humidity and air pressure), weather maps, satellites and radar.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.7.1 Understand the atmosphere and how the cycling of water relates to Earth’s
                weather and climate.
              </div>
              <div>ESS.7.1.5</div>
              <div>
                Use models to explain the influence of convection, global winds, and the jet stream
                on weather and climatic conditions.
              </div>
            </div>

            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.7.2 Understand the reciprocal relationship between the atmosphere and humans.
              </div>
              <div>ESS.7.2.1</div>
              <div>
                Engage in argument from evidence to explain that the good health of humans and
                environment requires: monitoring of the atmosphere, maintaining air quality and
                stewardship.
              </div>
            </div>

            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.7.2 Understand the reciprocal relationship between the atmosphere and humans.
              </div>
              <div>ESS.7.2.2</div>
              <div>
                Analyze and interpret data to explain how changes in the structure and composition
                of the atmosphere affects the greenhouse effect and global temperatures.
              </div>
            </div>

            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.7.2 Understand the reciprocal relationship between the atmosphere and humans.
              </div>
              <div>ESS.7.2.3</div>
              <div>
                Obtain, evaluate, and communicate information to explain the impacts on humans and
                mitigation strategies of potentially hazardous environmental factors (including air
                quality index, UV index, Heat Index, Wildfires) and storms (hurricanes, blizzards,
                tornadoes, severe thunderstorms, floods).
              </div>
            </div>
            <div className="row">
              <div>The Periodic Table</div>
              <div>8</div>
              <div>Matter and its Interactions</div>
              <div>
                PS.8.1 Understand the properties of matter and changes that occur when matter
                interacts in open and closed systems.
              </div>
              <div>PS.8.1.1</div>
              <div>
                Construct an explanation to classify matter as elements, compounds, or mixtures
                based on how the atoms are arranged in various substances.
              </div>
            </div>

            <div className="row">
              <div>The Periodic Table</div>
              <div>8</div>
              <div>Matter and its Interactions</div>
              <div>
                PS.8.1 Understand the properties of matter and changes that occur when matter
                interacts in open and closed systems.
              </div>
              <div>PS.8.1.2</div>
              <div>
                Use models to illustrate the structure of atoms in terms of the protons, electrons,
                and neutrons (using the location, charges and comparative size of these subatomic
                particles), without consideration of isotopes, ions, and energy levels.
              </div>
            </div>

            <div className="row">
              <div>The Periodic Table</div>
              <div>8</div>
              <div>Matter and its Interactions</div>
              <div>
                PS.8.1 Understand the properties of matter and changes that occur when matter
                interacts in open and closed systems.
              </div>
              <div>PS.8.1.3</div>
              <div>
                Analyze and interpret data to explain how the physical properties of elements and
                their reactivity have been used to produce the current model of the Periodic Table
                of Elements.
              </div>
            </div>

            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>8</div>
              <div>Matter and its Interactions</div>
              <div>
                PS.8.1 Understand the properties of matter and changes that occur when matter
                interacts in open and closed systems.
              </div>
              <div>PS.8.1.4</div>
              <div>
                Construct an explanation to classify changes in matter as physical changes
                (including changes in size, shape, and state) or chemical changes that are the
                result of a chemical reaction (including changes in energy, color, formation of a
                gas or precipitate).
              </div>
            </div>

            <div className="row">
              <div>Chemical Reactions</div>
              <div>8</div>
              <div>Matter and its Interactions</div>
              <div>
                PS.8.1 Understand the properties of matter and changes that occur when matter
                interacts in open and closed systems.
              </div>
              <div>PS.8.1.5</div>
              <div>
                Use models to illustrate how atoms are rearranged during a chemical reaction so that
                balanced chemical equations support the Law of Conservation of Mass (in both open
                and closed systems).
              </div>
            </div>

            <div className="row">
              <div>The Immune System</div>
              <div>8</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.8.1 Understand the hazards caused by agents of diseases that affect living
                organisms.
              </div>
              <div>LS.8.1.1</div>
              <div>
                Construct an explanation to compare the basic characteristics of viruses, bacteria,
                fungi and parasites relating to the spread, treatment and prevention of disease.
              </div>
            </div>

            <div className="row">
              <div>The Immune System</div>
              <div>8</div>
              <div>From Molecules to Organisms- Structures and Processes</div>
              <div>
                LS.8.1 Understand the hazards caused by agents of diseases that affect living
                organisms.
              </div>
              <div>LS.8.1.2</div>
              <div>
                Analyze and interpret data to explain the difference between epidemic and pandemic
                as it relates to the spread, treatment and prevention of disease.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>8</div>
              <div>Ecosystems- Interactions, Energy, and Dynamics</div>
              <div>
                LS.8.2 Understand how organisms interact with and respond to the biotic and abiotic
                factors in their environment.
              </div>
              <div>LS.8.2.1</div>
              <div>
                Carry out investigations to explain how changing biotic and abiotic factors such as
                food, water, shelter, and space affect populations in an ecosystem.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>8</div>
              <div>Ecosystems- Interactions, Energy, and Dynamics</div>
              <div>
                LS.8.2 Understand how organisms interact with and respond to the biotic and abiotic
                factors in their environment.
              </div>
              <div>LS.8.2.2</div>
              <div>
                Construct an explanation to summarize the relationships among producers, consumers,
                and decomposers including the positive and negative consequences of such
                interactions including: coexistence and cooperation, competition (predator/prey),
                parasitism, and mutualism.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>8</div>
              <div>Ecosystems- Interactions, Energy, and Dynamics</div>
              <div>
                LS.8.2 Understand how organisms interact with and respond to the biotic and abiotic
                factors in their environment.
              </div>
              <div>LS.8.2.3</div>
              <div>
                Construct an explanation to summarize how food provides the energy and the building
                materials required for the growth and survival of all organisms (to include plants).
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>8</div>
              <div>Ecosystems- Interactions, Energy, and Dynamics</div>
              <div>
                LS.8.2 Understand how organisms interact with and respond to the biotic and abiotic
                factors in their environment.
              </div>
              <div>LS.8.2.4</div>
              <div>
                Use models to explain how the flow of energy within food webs is interconnected with
                the cycling of matter (water and carbon).
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution- Unity and Diversity</div>
              <div>
                LS.8.3 Understand the evolution of organisms over time based on evidence and
                processes.
              </div>
              <div>LS.8.3.1</div>
              <div>
                Analyze and interpret data to infer evolutionary relationships by using evidence
                drawn from fossils and comparative anatomy.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution- Unity and Diversity</div>
              <div>
                LS.8.3 Understand the evolution of organisms over time based on evidence and
                processes.
              </div>
              <div>LS.8.3.2</div>
              <div>
                Use models to explain the process of natural selection, in which genetic variations
                in a population affect individuals’ likelihood of surviving and reproducing in its
                environment.
              </div>
            </div>

            <div className="row">
              <div>Human impacts on ecosystems</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                ESS.8.1 Understand the history of Earth and its life forms based on evidence of
                change recorded in fossil records and landforms.
              </div>
              <div>ESS.8.1.1</div>
              <div>
                Analyze and interpret data to conclude the relative age of Earth and relative age of
                rocks and fossils from index fossils and ordering of rock layers.
              </div>
            </div>

            <div className="row">
              <div>Human impacts on ecosystems</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>
                ESS.8.1 Understand the history of Earth and its life forms based on evidence of
                change recorded in fossil records and landforms.
              </div>
              <div>ESS.8.1.2</div>
              <div>
                Engage in argument from evidence to explain the use of fossils, composition of
                sedimentary rocks, faults, and igneous rock formations found in rock layers as
                evidence of the history of the Earth and its life forms.
              </div>
            </div>

            <div className="row">
              <div>The Water Cycle</div>
              <div>8</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.8.2 Understand the hydrosphere including freshwater, estuarine, ocean systems.
              </div>
              <div>ESS.8.2.1</div>
              <div>
                Use models to explain the structure of the hydrosphere including: water distribution
                on earth, local river basins, estuaries, and water availability.
              </div>
            </div>

            <div className="row">
              <div>The Water Cycle</div>
              <div>8</div>
              <div>Earth’s Systems</div>
              <div>
                ESS.8.2 Understand the hydrosphere including freshwater, estuarine, ocean systems.
              </div>
              <div>ESS.8.2.2</div>
              <div>
                Use models to explain how temperature and salinity drive major ocean currents and
                how these currents impact climate, ecosystems, and the distribution of nutrients,
                minerals, dissolved gases, and life forms.
              </div>
            </div>

            <div className="row">
              <div>The Water Cycle</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.3 Understand the reciprocal relationship between the hydrosphere and humans.
              </div>
              <div>ESS.8.3.1</div>
              <div>
                Analyze and interpret data to predict the safety and potability of water supplies in
                North Carolina based on physical and biological factors, including: temperature,
                dissolved oxygen, pH, nitrates and phosphates, turbidity, and bio-indicators.
              </div>
            </div>

            <div className="row">
              <div>The Water Cycle</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.3 Understand the reciprocal relationship between the hydrosphere and humans.
              </div>
              <div>ESS.8.3.2</div>
              <div>
                Engage in argument from evidence to explain that the good health of humans and the
                environment requires: monitoring of the hydrosphere, water quality standards,
                methods of water treatment, maintaining safe water quality, and stewardship.
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.4 Understand the environmental implications associated with the various
                methods of obtaining, managing, and using energy resources.
              </div>
              <div>ESS.8.4.1</div>
              <div>
                Construct an explanation to classify the primary sources of energy as either
                renewable (Geothermal, Biomass, Solar, Wind, Hydroelectric) or nonrenewable (Coal,
                Petroleum, Natural Gas, Nuclear).
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.4 Understand the environmental implications associated with the various
                methods of obtaining, managing, and using energy resources.
              </div>
              <div>ESS.8.4.2</div>
              <div>
                Engage in argument from evidence to explain the environmental consequences of the
                various methods of obtaining, transforming, and distributing energy.
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.4 Understand the environmental implications associated with the various
                methods of obtaining, managing, and using energy resources.
              </div>
              <div>ESS.8.4.3</div>
              <div>
                Analyze and interpret data to illustrate the relationship between human activities
                and global temperatures since industrialization.
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>
                ESS.8.4 Understand the environmental implications associated with the various
                methods of obtaining, managing, and using energy resources.
              </div>
              <div>ESS.8.4.4</div>
              <div>
                Obtain, evaluate, and communicate information to compare the long term implications
                of the use of renewable and nonrenewable energy resources and the importance of
                stewardship and conservation.
              </div>
            </div>
          </div>
        </div>
      </AlignmentSection>
    </NorthCarolinaAlignmentLayout>
  );
}

export default TwentyTwentyFourOregonPage;
